<template>
    <div class="w-full flex flex-none items-center justify-center rounded-br" :class="slim ? '' : 'p-24 bg-primary-5'">
        <div
            v-if="editor"
            class="w-full rounded border border-primary-20 trans-ease outline-none flex flex-col bg-white"
            :class="[ isUnfolded ? hasExternalPartner && can.manage ? 'h-288' : 'h-240' : 'h-48']"
            ref="focusTarget"
            tabindex="0"
            @focusin="handleFocusIn"
            @focusout="handleFocusOut"
        >
            <div v-show="isUnfolded" class="flex">
                <tabs v-if="hasExternalPartner && can.manage"
                      :active-key="activeItem"
                      :items="tabs"
                      @set-active="changeCommentItem($event)"
                ></tabs>
                <div
                    class="flex-1 flex items-center px-16 border-b border-primary-20"
                    :class="hasExternalPartner && can.manage ? 'w-1/2 justify-end' : 'w-full py-8'"
                >
                    <editor-button
                        v-if="editor.commands.setBold"
                        :active="editor.isActive('bold')"
                        @click="editor.commands.toggleBold()"
                    >
                        <svg-icon class="w-18 h-18" type="format-bold"></svg-icon>
                    </editor-button>

                    <editor-button
                        v-if="editor.commands.italic"
                        :active="editor.isActive('italic')"
                        @click="editor.commands.toggleItalic()"
                    >
                        <svg-icon class="w-18 h-18" type="format-italic"></svg-icon>
                    </editor-button>

                    <editor-button
                        v-if="editor.commands.setUnderline"
                        :active="editor.isActive('underline')"
                        @click="editor.commands.toggleUnderline()"
                    >
                        <svg-icon class="w-18 h-18" type="format-underlined"></svg-icon>
                    </editor-button>

                    <editor-button
                        v-if="editor.commands.setStrike"
                        :active="editor.isActive('strike')"
                        @click="editor.commands.toggleStrike()"
                    >
                        <svg-icon class="w-18 h-18" type="format-strikethrough"></svg-icon>
                    </editor-button>

                    <editor-button
                        v-if="editor.commands.toggleBulletList"
                        :active="editor.isActive('bulletList')"
                        @click="editor.commands.toggleBulletList()"
                    >
                        <svg-icon class="w-18 h-18" type="format-list-bulleted"></svg-icon>
                    </editor-button>

                    <editor-button
                        v-if="editor.commands.toggleOrderedList"
                        :active="editor.isActive('orderedList')"
                        @click="editor.commands.toggleOrderedList()"
                    >
                        <svg-icon class="w-18 h-18" type="format-list-numbered"></svg-icon>
                    </editor-button>

                    <editor-button @click="triggerSuggestion">
                        <svg-icon class="w-18 h-18" type="alternate-email"></svg-icon>
                    </editor-button>

                    <emoji-button small @select="insertEmoji"></emoji-button>
                </div>
            </div>

            <editor-content
                :editor="editor"
                class="comment__content flex-grow overflow-y-auto"
                :class="[ isUnfolded ? 'unfolded' : '' ]"
            />

            <div v-show="isUnfolded" class="flex items-center p-16">
                <simple-dropdown
                    v-if="activeItem === 'internal' && !slim"
                    on-top
                    position="left"
                >
                    <template #trigger>
                        <simple-button
                            class="mr-8"
                            icon="visibility"
                            :loading="submittingComment"
                            look="secondary"
                            small
                        >
                            <template v-if="visibleFor !== 'selected'">
                                {{ visibilityOptions.find(i => i.key === visibleFor).title }}
                            </template>
                            <template v-else>
                                    <span class="flex items-center">
                                        <span class="mr-4">{{ $t('timeline.only_visible_for') }}</span>
                                        <user-avatars :users="selectedUsers" display="inline-flex" :size="24"/>
                                    </span>
                            </template>
                        </simple-button>
                    </template>
                    <template #content>
                        <div class="bg-white rounded-6 w-360 overflow-hidden -space-y-px">
                            <div v-for="(option, i) in visibilityOptions" :key="option.key"
                                 :class="[option.key === visibleFor ? 'bg-secondary-5 border-secondary z-10' : 'border-primary-20', i === 0 ? 'rounded-t' : '', i === visibilityOptions.length - 1 ? 'rounded-b' : '']"
                                 class="relative border p-16 flex cursor-pointer"
                                 @click="option.fn">
                                <div class="flex items-center h-12">
                                    <input :checked="option.key === visibleFor"
                                           class="form-radio h-16 w-16 text-secondary trans-ease rounded-32 border-primary-20 custom-radio"
                                           type="radio">
                                </div>
                                <div class="ml-8 flex flex-col cursor-pointer">
                                        <span
                                            :class="option.key === visibleFor ? 'text-secondary' : 'text-primary-80'"
                                            class="block leading-5">
                                            {{ option.title }}
                                        </span>
                                    <span v-if="option.key !== 'selected'"
                                          :class="option.key === visibleFor ? 'text-secondary-80' : 'text-primary-80'"
                                          class="block text-body-sm leading-5 mt-2">
                                            {{ option.description }}
                                        </span>
                                    <div v-else class="mt-8 w-240">
                                        <user-avatars :users="selectedUsers" size="small" left/>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>
                </simple-dropdown>

                <simple-button
                    v-if="slim"
                    small
                    @click="$emit('cancel')"
                >
                    {{ $t('general.cancel') }}
                </simple-button>

                <simple-button
                    class="ml-auto"
                    :disabled="!filteredHtmlContent.length"
                    display="secondary"
                    :loading="submittingComment"
                    look="primary"
                    small
                    @click="submitComment"
                >
                    <span v-if="comment">{{ $t('wysiwyg.update_comment') }}</span>
                    <span v-else-if="activeItem === 'internal'">{{ $t('wysiwyg.add_comment') }}</span>
                    <span v-else>{{ $t('wysiwyg.add_comment_external') }}</span>
                </simple-button>
            </div>

            <div ref="mention_user" class="tooltip relative shadow">
                <div v-if="showSuggestions">
                    <div class="bg-white rounded-sm overflow-hidden text-body-sm font-semibold">
                        <ul class="py-4 overflow-y-auto max-h-200 relative" ref="mentionContainer">
                            <li
                                v-for="(suggestion, index) in mentionSuggestions"
                                :key="suggestion.id"
                                :ref="'suggestion-' + index"
                            >
                                <button
                                    class="flex items-center py-4 pl-8 w-full"
                                    @mouseover="selectSuggestion(index)"
                                    @click="commitSuggestion"
                                    :class="[ index === selectedSuggestion ? 'bg-primary-40 text-white' : '' ]"
                                >
                                    <avatar :image="suggestion.image_url" size="small" class="mr-8">
                                        {{ suggestion.initials }}
                                    </avatar>

                                    <span class="mr-16 font-semibold">{{ suggestion.name }}</span>
                                </button>

                            </li>
                        </ul>
                    </div>
                </div>
            </div>

        </div>

        <popup-modal v-if="showUserSelection" @close="closeUserSelectionAndFocus">
            <p class="font-semibold text-body mb-16 text-center">
                {{ $t('wysiwyg.user_selection_title') }}
            </p>
            <div class="-space-y-1">
                <checkbox v-for="(user, i) in users" :key="user.id" slot-before
                          :label-class="`p-8 border -space-y-px relative ${selectedUserIds.includes(user.id) ? 'bg-secondary-5 border-secondary z-10' : 'border-primary-20 z-0'} ${i === 0 ? 'rounded-t' : ''} ${i === users.length - 1 ? 'rounded-b' : ''}`"
                          v-model="selectedUserIds" :value="user.id" span-class="flex-grow">
                    <div class="flex items-center">
                        <avatar :image="user.image_url" size="small" class="mr-8">
                            {{ user.initials }}
                        </avatar>
                        <p>{{ user.name }}</p>
                    </div>
                </checkbox>
            </div>

            <simple-button @click="closeUserSelectionAndFocus" display="primary" class="mt-16 ml-auto">
                {{ $t('wysiwyg.user_selection_button') }}
            </simple-button>
        </popup-modal>
    </div>
</template>

<script>
import { Editor, EditorContent } from '@tiptap/vue-3';
import Bold from '@tiptap/extension-bold';
import BulletList from '@tiptap/extension-bullet-list';
import Document from '@tiptap/extension-document';
import HardBreak from '@tiptap/extension-hard-break';
import History from '@tiptap/extension-history';
import Italic from '@tiptap/extension-italic';
import ListItem from '@tiptap/extension-list-item';
import OrderedList from '@tiptap/extension-ordered-list';
import Paragraph from '@tiptap/extension-paragraph';
import Placeholder from '@tiptap/extension-placeholder';
import Strike from '@tiptap/extension-strike';
import Text from '@tiptap/extension-text';
import Underline from '@tiptap/extension-underline';
import SvgIcon from '../ui/SvgIcon.vue';
import SimpleButton from '../ui/SimpleButton.vue';
import EditorButton from '../ui/EditorButton.vue';
import {createPopper} from '@popperjs/core';
import {mapState} from 'pinia';
import Avatar from '../ui/Avatar.vue';
import * as comments from '../../services/CommentService';
import * as interimComments from '../../services/InterimCommentService';
import Comment from '../../models/Comment';
import InterimComment from '../../models/InterimComment';
import {normalizeJson} from '../../../shared/helpers';
import EmojiButton from './EmojiButton.vue';
import Checkbox from '../forms/Checkbox';
import Tabs from '../navigation/Tabs';
import SimpleDropdown from "./SimpleDropdown";
import PopupModal from "./PopupModal";
import UserAvatars from "./UserAvatars";
import User from "../../models/User";
import DropdownItem from "./DropdownItem.vue";
import {useAuthenticationStore} from "../../state/modules/authentication";
import {useRepo} from "pinia-orm";
import CommentBoxMention from "../../etc/mention.js";

export default {
   components: {
       DropdownItem,
       UserAvatars,
       PopupModal,
       SimpleDropdown,
       Tabs,
       Checkbox,
       'editor-content': EditorContent,
       'svg-icon': SvgIcon,
       'simple-button': SimpleButton,
       'editor-button': EditorButton,
       'avatar': Avatar,
       'emoji-button': EmojiButton,
   },
   props: {
       application: {
           type: Object,
           required: true,
       },
       comment: Object,
       slim: Boolean
   },
   data: function () {
       return {
           editor: null,
           showMention: false,
           mentionSearch: null,
           selectedSuggestion: null,

           htmlContent: this.comment ? this.comment.text : '',
           visibleFor: 'all',
           selectedUserIds: [],
           showUserSelection: false,

           insertMention: () => {},
           submittingComment: false,
           activeItem: 'internal',
       }
   },
   computed: {
       ...mapState(useAuthenticationStore, ['currentUserId', 'currentCompanyId']),
       can() {
           return {
               manage: this.$can([
                   'applications.manage',
                   'applications.manage.' + this.$route.params.id,
                   'job-applications.manage.' + this.application?.stage?.job?.id,
               ]),
           };
       },
       users() {
           return User.allUsersExceptMe();
       },
       selectedUsers() {
           return this.users.filter(i => this.selectedUserIds.includes(i.id))
       },
       mentionSuggestions() {
           return this.users.filter(user => {
               return user.name.toLowerCase().includes(this.mentionSearch.toLowerCase());
           })
       },
       showSuggestions() {
           return this.showMention && (this.mentionSearch !== null) && this.mentionSuggestions.length;
       },
       isUnfolded() {
           return this.editor && (this.editor.focused || this.htmlContent.length);
       },
       filteredHtmlContent() {
           return this.htmlContent === '<p></p>' ? '' : this.htmlContent;
       },
       hasExternalPartner() {
           return !!this.application.interim_candidate_id;
       },
       tabs() {
           return {
               'internal': {
                   'title': this.$t('wysiwyg.tab_internal'),
                   'icon': '',
               },
               'external': {
                   'title': this.$t('wysiwyg.tab_external'),
                   'icon': '',
               },
           };
       },
       visibilityOptions() {
           return [
               {
                   key: 'all',
                   title: this.$t('wysiwyg.visibilityOptions.all'),
                   description: this.$t('wysiwyg.visibilityOptions.all_desc'),
                   fn: () => this.visibleFor = 'all'
               },
               {
                   key: 'selected',
                   title: this.$t('wysiwyg.visibilityOptions.selected'),
                   description: this.$t('wysiwyg.visibilityOptions.selected_desc'),
                   fn: () => {
                       this.visibleFor = 'selected';
                       this.showUserSelection = true;
                   }
               },
               {
                   key: 'me',
                   title: this.$t('wysiwyg.visibilityOptions.me'),
                   description: this.$t('wysiwyg.visibilityOptions.me_desc'),
                   fn: () => this.visibleFor = 'me'
               }
           ]
       },
   },
   methods: {
       changeCommentItem(event) {
           this.activeItem = event;

           if (event === 'external') {
               this.editor.extensions.options.placeholder.emptyNodeText = this.$t('wysiwyg.create_comment_external') + ' …';
           } else {
               this.editor.extensions.options.placeholder.emptyNodeText = this.$t('wysiwyg.create_comment') + ' …';
           }
       },
       triggerSuggestion() {

           let {view, state} = this.editor;
           let before = state.selection.$from.nodeBefore;
           let after = state.selection.$to.nodeAfter;

           let insert = '';
           let moveCursor = false;

           if (before && before.type.isText && before.text.substr(before.text.length - 1) != ' ') {
               insert += ' ';
           }

           insert += '@';

           if (after && after.type.isText && after.text.substr(after.text.length - 1) != ' ') {
               insert += ' ';
               moveCursor = true;
           }

           this.editor.commands.insertContent(insert);
           this.editor.chain().focus();

           if (moveCursor) {
               let newPos = this.editor.selection.from - 1;
               this.editor.setSelection(newPos, newPos);
           }
       },
       createPopper(target) {
           this._popper = createPopper(target, this.$refs.mention_user, {
               placement: 'bottom-start',
               modifiers: [
                   {
                       name: 'offset',
                       options: {
                           offset: [0, 8],
                       }
                   }
               ]
           });
       },
       destroyPopper() {
           if (this._popper) {
               this._popper.destroy();
           }
       },
       selectSuggestion(index) {
           this.selectedSuggestion = index;
       },
       selectPreviousSuggestion() {
           let previousSuggestion = this.selectedSuggestion - 1;

           if (previousSuggestion < 0) {
               this.selectedSuggestion = this.mentionSuggestions.length - 1;
           } else {
               this.selectedSuggestion = previousSuggestion;
           }

           this.scrollIntoView();
       },
       selectNextSuggestion() {
           let nextSuggestion = this.selectedSuggestion + 1;

           if (nextSuggestion >= this.mentionSuggestions.length) {
               this.selectedSuggestion = 0;
           } else {
               this.selectedSuggestion = nextSuggestion;
           }

           this.scrollIntoView();
       },
       scrollIntoView() {
           let container = this.$refs.mentionContainer;
           let el = this.$refs['suggestion-' + this.selectedSuggestion][0];

           if (el.offsetTop < container.scrollTop) {
               container.scrollTop = el.offsetTop;
           }

           if (el.offsetTop + el.clientHeight > container.clientHeight + container.scrollTop) {
               container.scrollTop = el.offsetTop - (container.clientHeight - el.clientHeight);
           }
       },
       commitSuggestion() {
           let user = this.mentionSuggestions[this.selectedSuggestion];
           if (user) {
               this.insertMention({ id: user.id, label: user.name });
               this.editor.chain().focus();
           }
       },
       submitComment() {
           this.submittingComment = true;

           if (this.comment) {
               comments.update(this.comment.id, {
                   data: { attributes: { text: this.htmlContent, }, },
               }).then(result => {
                   return useRepo(Comment).save(normalizeJson(result));
               }).finally(() => {
                   this.submittingComment = false;
                   this.$emit('cancel');
               });

               return;
           }

           if (this.activeItem === 'external') {
               interimComments.store(this.application.id, {
                   data: {
                       attributes: {
                           company_id: this.currentCompanyId,
                           comment: this.htmlContent,
                       }
                   }
               }).then(result => {
                   return useRepo(InterimComment).save(normalizeJson(result))
               }).then(inserts => {
                   this.submittingComment = false;
                   this.editor.commands.setContent('');
                   this.htmlContent = '';
                   this.editor.commands.blur();
               })
           } else {
               comments.store(this.application.id, {
                   data: {
                       attributes: {
                           text: this.htmlContent,
                           visibility: this.visibleFor
                       },
                       relationships: {
                           application: {
                               data: {
                                   type: 'applications',
                                   id: this.application.id,
                               }
                           },
                           user: {
                               data: {
                                   type: 'users',
                                   id: this.currentUserId,
                               }
                           },
                           users: {
                               data: this.selectedUserIds.map(id => ({
                                   type: 'users',
                                   id
                               }))
                           }
                       }
                   },
               }).then(result => {
                   if (this.$can('subscriptions.view')) {
                       let data = normalizeJson(result);

                       if (data.mentionedUsers.length) {
                           User.allUsersExceptMe();
                       }
                   }

                   return useRepo(Comment).save(normalizeJson(result));
               }).then(inserts => {
                   this.editor.commands.setContent('');
                   this.htmlContent = '';
                   this.editor.commands.blur();
               }).finally(() => {
                   this.submittingComment = false;
               })
           }

           this.$nextTick(() => this.scrollToEnd())
       },
       insertEmoji(emoji) {
           this.editor.commands.insertContent(emoji);
           this.editor.chain().focus();
       },
       removeFocus() {
           if (this.htmlContent === '<p></p>') {
               this.htmlContent = '';
           }
       },
       handleFocusIn(e) {
           if (e.target === this.$refs.focusTarget) {
               this.editor.chain().focus();
           }

           this.htmlContent = this.htmlContent || '<p></p>';

           if (!this.comment) {
               this.$nextTick(() => this.scrollToEnd())
           }
       },
       handleFocusOut(e) {
           setTimeout(() => {
               if (this.$refs.focusTarget && !this.$refs.focusTarget.contains(document.activeElement)) {
                   this.removeFocus();
               }
           }, 0);
       },
       closeUserSelectionAndFocus() {
           this.showUserSelection = false;
           this.$refs.focusTarget.focus();
       },
       scrollToEnd() {
           let content = document.getElementById('timeline-container');
           content.scrollTop = content.scrollHeight + 200;
       },
   },
   mounted() {
       User.fetchUsers();

       this.editor = new Editor({
           content: this.htmlContent,
           parseOptions: {
               preserveWhitespace: 'full',
           },
           autofocus: false,
           extensions: [
               Placeholder.configure({
                   placeholder: this.$t('wysiwyg.create_comment') + ' …',
               }),
               Document,
               Paragraph,
               Text,
               Underline,
               Strike,
               Bold,
               Italic,
               History,
               HardBreak,
               BulletList,
               OrderedList,
               ListItem,
               CommentBoxMention.configure({
                   HTMLAttributes: {
                       class: "mention"
                   },
                   suggestion: {
                       char: '@',
                       items: ({ query }) => {
                           this.mentionSearch = query;
                           return this.mentionSuggestions;
                       },
                       render: () => {
                           const parent = this;
                           return {
                               onStart(props) {
                                   parent.showMention = true;
                                   parent.mentionSearch = props.query;
                                   parent.createPopper(props.decorationNode);
                                   parent.selectedSuggestion = 0;

                                   // we save the command for inserting a selected mention
                                   // this allows us to call it inside of our custom popup
                                   // via keyboard navigation and on click
                                   parent.insertMention = props.command;
                               },
                               onUpdate(props) {
                                   parent.mentionSearch = props.query;
                                   parent.selectedSuggestion = 0;

                                   // the range is embedded in the command so we need to update it
                                   parent.insertMention = props.command;
                               },
                               onExit() {
                                   parent.destroyPopper();
                                   parent.mentionSearch = null;
                                   parent.selectedSuggestion = 0;
                                   parent.showMention = false;
                               },
                               onKeyDown(props) {
                                   const event = props.event;
                                   if (event.keyCode === 38) {
                                       parent.selectPreviousSuggestion();
                                       return true;
                                   }
                                   // pressing down arrow
                                   if (event.keyCode === 40) {
                                       parent.selectNextSuggestion();
                                       return true;
                                   }
                                   // pressing enter
                                   if (event.keyCode === 13) {
                                       parent.commitSuggestion();
                                       return true;
                                   }
                               }
                           };
                       }
                    }
               }),
           ],
           onUpdate: ({ editor }) => {
               this.htmlContent = editor.getHTML();
           }
       });
   },
   beforeUnmount() {
       this.editor.destroy();
   },
}
</script>

<style>

.tiptap p.is-editor-empty:first-child::before {
    content: attr(data-placeholder);
    float: left;
    color: #aaa;
    pointer-events: none;
    height: 0;
    font-style: italic;
}

.tiptap {
    &.ProseMirror {
        @apply p-12 items-center outline-none h-full leading-none;
    }

    &.unfolded .ProseMirror {
        @apply min-h-full leading-normal;
    }
}
</style>

<style scoped>
[type='radio']:checked {
    background-image: url("data:image/svg+xml,%3csvg viewBox='0 0 16 16' fill='white' xmlns='http://www.w3.org/2000/svg'%3e%3ccircle cx='8' cy='8' r='3'/%3e%3c/svg%3e");
}

[type='radio']:checked {
    border-color: transparent;
    background-color: currentColor;
    background-size: 100% 100%;
    background-position: center;
    background-repeat: no-repeat;
}

[type='checkbox'], [type='radio'] {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    padding: 0;
    -webkit-print-color-adjust: exact;
    color-adjust: exact;
    display: inline-block;
    vertical-align: middle;
    background-origin: border-box;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    flex-shrink: 0;
    background-color: #fff;
    border-width: 1px;
}

ul {
    list-style-type: unset !important;
    padding-left: unset !important;
}
</style>
